import { useState, useEffect, useRef } from 'react';
import {
  HeartIcon,
  ShieldCheckIcon,
  DocumentIcon,
  PlusIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/Card';
import { Button } from '../ui/Button';
import { DateInput } from '../ui/DateInput';
import ImageUpload from '../ui/ImageUpload';
import ImagePreviewModal from '../ui/ImagePreviewModal';
import { cn } from '@/utils/cn';
import { getFullImageUrl } from '@/utils/imageUrl';
import { executiveProtectionService, type MedicalAttachment } from '@/services/executive-protection.service';

export interface MedicalData {
  insuranceProvider?: string;
  insurancePolicyNumber?: string;
  insuranceGroupNumber?: string;

  primaryDoctorName?: string;
  primaryDoctorPhone?: string;
  primaryDoctorAddress?: string;

  medicalConditions?: string[];

  medicalDisclosureConsent?: boolean;
  consentDate?: string;
  consentExpiresAt?: string;
}

// interface MedicalAttachment removed, using imported type

interface MedicalInformationSectionProps {
  isEditing: boolean;
  medicalData: MedicalData;
  onMedicalDataChange: (data: Partial<MedicalData>) => void;
  onInsuranceCardUpload?: (file: File, type: 'front' | 'back') => Promise<void>;
  onConsentFormUpload?: (file: File) => Promise<void>;
  errors?: Partial<Record<keyof MedicalData, string>>;
  personId?: string; // ID to fetch existing medical attachments
}

export default function MedicalInformationSection({
  isEditing,
  medicalData,
  onMedicalDataChange,
  onInsuranceCardUpload,
  onConsentFormUpload,
  errors = {},
  personId,
}: MedicalInformationSectionProps) {
  const [newCondition, setNewCondition] = useState('');
  const [isExpanded, setIsExpanded] = useState(false); // Collapsed by default for privacy
  const [showPolicyNumber, setShowPolicyNumber] = useState(false);
  const [showGroupNumber, setShowGroupNumber] = useState(false);
  const [medicalAttachments, setMedicalAttachments] = useState<MedicalAttachment[]>([]);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const isFetchingRef = useRef(false); // Track if fetch is in progress

  // Helper to format date for HTML5 date input (yyyy-MM-dd)
  const formatDateForInput = (dateString?: string): string => {
    if (!dateString) { return ''; }
    // Extract just the date portion from ISO timestamp (e.g., "2025-01-21T06:27:35.708Z" → "2025-01-21")
    return dateString.split('T')[0];
  };

  // Fetch medical attachments when personId is provided
  const fetchMedicalAttachments = async () => {
    if (!personId || isFetchingRef.current) { return; }

    isFetchingRef.current = true;
    try {
      const attachments = await executiveProtectionService.getMedicalAttachments(personId);
      setMedicalAttachments(attachments ?? []);
    } catch (error) {
      console.error('Failed to fetch medical attachments:', error);
    } finally {
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    if (personId) {
      // Fetch attachments only when personId changes, not on edit mode toggle
      // Using ref to prevent concurrent fetches that can trigger rate limits
      void fetchMedicalAttachments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personId]); // Removed isEditing to prevent redundant fetches

  // Helper to get attachment by type
  const getAttachmentByType = (type: string): MedicalAttachment | undefined => {
    return medicalAttachments.find(att => att.attachmentType === type);
  };

  const insuranceCardFront = getAttachmentByType('INSURANCE_CARD_FRONT');
  const insuranceCardBack = getAttachmentByType('INSURANCE_CARD_BACK');
  const consentForm = getAttachmentByType('CONSENT_FORM');

  const handleDownloadAttachment = (attachment: MedicalAttachment) => {
    const url = `${getFullImageUrl(attachment.filePath)}`;
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    // eslint-disable-next-line no-alert
    if (!personId || !confirm('Are you sure you want to delete this attachment?')) { return; }

    try {
      await executiveProtectionService.deleteMedicalAttachment(personId, attachmentId);
      // Refresh attachments
      void fetchMedicalAttachments();
    } catch (error) {
      console.error('Failed to delete attachment:', error);
      // eslint-disable-next-line no-alert
      alert('Failed to delete attachment');
    }
  };

  const addMedicalCondition = () => {
    if (newCondition.trim()) {
      const conditions = medicalData.medicalConditions ?? [];
      onMedicalDataChange({
        medicalConditions: [...conditions, newCondition.trim()],
      });
      setNewCondition('');
    }
  };

  const removeMedicalCondition = (index: number) => {
    const conditions = medicalData.medicalConditions ?? [];
    onMedicalDataChange({
      medicalConditions: conditions.filter((_, i) => i !== index),
    });
  };

  const isConsentExpiringSoon = () => {
    if (!medicalData.consentExpiresAt) { return false; }
    const expiresAt = new Date(medicalData.consentExpiresAt);
    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    return expiresAt <= thirtyDaysFromNow && expiresAt > now;
  };

  const isConsentExpired = () => {
    if (!medicalData.consentExpiresAt) { return false; }
    return new Date(medicalData.consentExpiresAt) < new Date();
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) { return 'Not specified'; }
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (!isEditing && !isExpanded) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center">
                <HeartIcon className="w-5 h-5 mr-2" />
                Medical Information
              </CardTitle>
              <CardDescription>Insurance, medical conditions, and consent information</CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExpanded(true)}
            >
              View Details
            </Button>
          </div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center">
              <HeartIcon className="w-5 h-5 mr-2" />
              Medical Information
            </CardTitle>
            <CardDescription>Insurance, medical conditions, and consent information</CardDescription>
          </div>
          {!isEditing && isExpanded && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExpanded(false)}
            >
              Hide Details
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Consent Status Warning */}
        {!isEditing && medicalData.medicalDisclosureConsent && (isConsentExpiringSoon() || isConsentExpired()) && (
          <div className={cn(
            'p-4 rounded-lg border-l-4',
            isConsentExpired()
              ? 'bg-danger-50 dark:bg-danger-900/20 border-danger-500'
              : 'bg-warning-50 dark:bg-warning-900/20 border-warning-500'
          )}>
            <div className="flex items-start">
              <ExclamationTriangleIcon className={cn(
                'w-5 h-5 mt-0.5',
                isConsentExpired() ? 'text-danger-600 dark:text-danger-400' : 'text-warning-600 dark:text-warning-400'
              )} />
              <div className="ml-3">
                <h3 className={cn(
                  'text-sm font-medium',
                  isConsentExpired() ? 'text-danger-800 dark:text-danger-300' : 'text-warning-800 dark:text-warning-300'
                )}>
                  {isConsentExpired() ? 'Medical Consent Expired' : 'Medical Consent Expiring Soon'}
                </h3>
                <p className={cn(
                  'text-sm mt-1',
                  isConsentExpired() ? 'text-danger-700 dark:text-danger-400' : 'text-warning-700 dark:text-warning-400'
                )}>
                  {isConsentExpired()
                    ? `Consent expired on ${formatDate(medicalData.consentExpiresAt)}. Please obtain updated consent.`
                    : `Consent expires on ${formatDate(medicalData.consentExpiresAt)}. Please renew consent soon.`
                  }
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Insurance Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-white flex items-center">
            <ShieldCheckIcon className="w-5 h-5 mr-2" />
            Insurance Information
          </h3>

          {!isEditing ? (
            <>
              {medicalData.insuranceProvider || medicalData.insurancePolicyNumber || insuranceCardFront || insuranceCardBack ? (
                <div className="space-y-3">
                  {medicalData.insuranceProvider && (
                    <div>
                      <div className="text-sm text-secondary-600 dark:text-secondary-400">Provider</div>
                      <div className="font-medium text-secondary-900 dark:text-white">{medicalData.insuranceProvider}</div>
                    </div>
                  )}
                  {medicalData.insurancePolicyNumber && (
                    <div>
                      <div className="text-sm text-secondary-600 dark:text-secondary-400">Policy Number</div>
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-secondary-900 dark:text-white">
                          {showPolicyNumber
                            ? medicalData.insurancePolicyNumber
                            : `•••• ${medicalData.insurancePolicyNumber.slice(-4)}`}
                        </div>
                        <button
                          onClick={() => setShowPolicyNumber(!showPolicyNumber)}
                          className="p-1 text-secondary-500 hover:text-secondary-700 dark:text-secondary-400 dark:hover:text-secondary-200 hover:bg-secondary-100 dark:hover:bg-secondary-800 rounded transition-colors"
                          title={showPolicyNumber ? 'Hide policy number' : 'Show policy number'}
                        >
                          {showPolicyNumber ? (
                            <EyeSlashIcon className="w-4 h-4" />
                          ) : (
                            <EyeIcon className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                  {medicalData.insuranceGroupNumber && (
                    <div>
                      <div className="text-sm text-secondary-600 dark:text-secondary-400">Group Number</div>
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-secondary-900 dark:text-white">
                          {showGroupNumber
                            ? medicalData.insuranceGroupNumber
                            : `•••• ${medicalData.insuranceGroupNumber.slice(-4)}`}
                        </div>
                        <button
                          onClick={() => setShowGroupNumber(!showGroupNumber)}
                          className="p-1 text-secondary-500 hover:text-secondary-700 dark:text-secondary-400 dark:hover:text-secondary-200 hover:bg-secondary-100 dark:hover:bg-secondary-800 rounded transition-colors"
                          title={showGroupNumber ? 'Hide group number' : 'Show group number'}
                        >
                          {showGroupNumber ? (
                            <EyeSlashIcon className="w-4 h-4" />
                          ) : (
                            <EyeIcon className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Insurance Card Images */}
                  {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
                  {(insuranceCardFront || insuranceCardBack) && (
                    <div className="pt-3 border-t border-secondary-200 dark:border-secondary-700">
                      <div className="text-sm text-secondary-600 dark:text-secondary-400 mb-2">Insurance Card Images</div>
                      <div className="grid grid-cols-2 gap-3">
                        {insuranceCardFront && (
                          <div>
                            <p className="text-xs text-secondary-600 dark:text-secondary-400 mb-2">Front</p>
                            <button
                              onClick={() => setPreviewImage(getFullImageUrl(insuranceCardFront.filePath) ?? null)}
                              className="group relative w-full aspect-video bg-secondary-100 dark:bg-secondary-800 rounded-lg overflow-hidden border-2 border-secondary-200 dark:border-secondary-700 hover:border-primary-400 dark:hover:border-primary-500 transition-all"
                            >
                              <img
                                src={getFullImageUrl(insuranceCardFront.filePath) ?? ''}
                                alt="Insurance Card Front"
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <PhotoIcon className="w-8 h-8 text-white" />
                              </div>
                            </button>
                          </div>
                        )}
                        {insuranceCardBack && (
                          <div>
                            <p className="text-xs text-secondary-600 dark:text-secondary-400 mb-2">Back</p>
                            <button
                              onClick={() => setPreviewImage(getFullImageUrl(insuranceCardBack.filePath) ?? null)}
                              className="group relative w-full aspect-video bg-secondary-100 dark:bg-secondary-800 rounded-lg overflow-hidden border-2 border-secondary-200 dark:border-secondary-700 hover:border-primary-400 dark:hover:border-primary-500 transition-all"
                            >
                              <img
                                src={getFullImageUrl(insuranceCardBack.filePath) ?? ''}
                                alt="Insurance Card Back"
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <PhotoIcon className="w-8 h-8 text-white" />
                              </div>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-secondary-500 dark:text-secondary-400">No insurance information on file</p>
              )}
            </>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Insurance Provider
                </label>
                <input
                  type="text"
                  value={medicalData.insuranceProvider ?? ''}
                  onChange={(e) => onMedicalDataChange({ insuranceProvider: e.target.value })}
                  className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                  placeholder="e.g., Blue Cross Blue Shield"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                    Policy Number
                  </label>
                  <input
                    type="text"
                    value={medicalData.insurancePolicyNumber ?? ''}
                    onChange={(e) => onMedicalDataChange({ insurancePolicyNumber: e.target.value })}
                    className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                    placeholder="Policy #"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                    Group Number
                  </label>
                  <input
                    type="text"
                    value={medicalData.insuranceGroupNumber ?? ''}
                    onChange={(e) => onMedicalDataChange({ insuranceGroupNumber: e.target.value })}
                    className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                    placeholder="Group #"
                  />
                </div>
              </div>

              {/* Insurance Card Upload */}
              {onInsuranceCardUpload && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300">
                    Insurance Card Images
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-secondary-600 dark:text-secondary-400 mb-2">Front</p>
                      {insuranceCardFront ? (
                        <div className="space-y-2">
                          <button
                            onClick={() => setPreviewImage(getFullImageUrl(insuranceCardFront.filePath) ?? null)}
                            className="group relative w-full aspect-video bg-secondary-100 dark:bg-secondary-800 rounded-lg overflow-hidden border-2 border-secondary-200 dark:border-secondary-700 hover:border-primary-400 dark:hover:border-primary-500 transition-all"
                          >
                            <img
                              src={getFullImageUrl(insuranceCardFront.filePath) ?? ''}
                              alt="Insurance Card Front"
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <PhotoIcon className="w-8 h-8 text-white" />
                            </div>
                          </button>
                          <div className="flex items-center justify-between">
                            <div className="text-xs text-secondary-600 dark:text-secondary-400">
                              Current: {insuranceCardFront.filename}
                            </div>
                            <button
                              onClick={() => handleDeleteAttachment(insuranceCardFront.id)}
                              className="p-1 text-danger-600 hover:text-danger-700 dark:text-danger-400 dark:hover:text-danger-300 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded transition-colors"
                              title="Delete attachment"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <ImageUpload
                          onImageSelect={async (file) => {
                            if (onInsuranceCardUpload) {
                              await onInsuranceCardUpload(file, 'front');
                              // Refresh attachments locally to show the uploaded file
                              void fetchMedicalAttachments();
                            }
                          }}
                          maxSizeMB={50}
                          acceptedFormats={['image/jpeg', 'image/jpg', 'image/png', 'application/pdf']}
                        />
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-secondary-600 dark:text-secondary-400 mb-2">Back</p>
                      {insuranceCardBack ? (
                        <div className="space-y-2">
                          <button
                            onClick={() => setPreviewImage(getFullImageUrl(insuranceCardBack.filePath) ?? null)}
                            className="group relative w-full aspect-video bg-secondary-100 dark:bg-secondary-800 rounded-lg overflow-hidden border-2 border-secondary-200 dark:border-secondary-700 hover:border-primary-400 dark:hover:border-primary-500 transition-all"
                          >
                            <img
                              src={getFullImageUrl(insuranceCardBack.filePath) ?? ''}
                              alt="Insurance Card Back"
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <PhotoIcon className="w-8 h-8 text-white" />
                            </div>
                          </button>
                          <div className="flex items-center justify-between">
                            <div className="text-xs text-secondary-600 dark:text-secondary-400">
                              Current: {insuranceCardBack.filename}
                            </div>
                            <button
                              onClick={() => handleDeleteAttachment(insuranceCardBack.id)}
                              className="p-1 text-danger-600 hover:text-danger-700 dark:text-danger-400 dark:hover:text-danger-300 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded transition-colors"
                              title="Delete attachment"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <ImageUpload
                          onImageSelect={async (file) => {
                            if (onInsuranceCardUpload) {
                              await onInsuranceCardUpload(file, 'back');
                              // Refresh attachments locally to show the uploaded file
                              void fetchMedicalAttachments();
                            }
                          }}
                          maxSizeMB={50}
                          acceptedFormats={['image/jpeg', 'image/jpg', 'image/png', 'application/pdf']}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Primary Doctor Information */}
        <div className="pt-4 border-t border-secondary-200 dark:border-secondary-700 space-y-4">
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Primary Doctor</h3>

          {!isEditing ? (
            <>
              {medicalData.primaryDoctorName ? (
                <div className="space-y-3">
                  <div>
                    <div className="text-sm text-secondary-600 dark:text-secondary-400">Name</div>
                    <div className="font-medium text-secondary-900 dark:text-white">{medicalData.primaryDoctorName}</div>
                  </div>
                  {medicalData.primaryDoctorPhone && (
                    <div>
                      <div className="text-sm text-secondary-600 dark:text-secondary-400">Phone</div>
                      <div className="font-medium text-secondary-900 dark:text-white">{medicalData.primaryDoctorPhone}</div>
                    </div>
                  )}
                  {medicalData.primaryDoctorAddress && (
                    <div>
                      <div className="text-sm text-secondary-600 dark:text-secondary-400">Address</div>
                      <div className="font-medium text-secondary-900 dark:text-white">{medicalData.primaryDoctorAddress}</div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-secondary-500 dark:text-secondary-400">No primary doctor on file</p>
              )}
            </>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Doctor Name
                </label>
                <input
                  type="text"
                  value={medicalData.primaryDoctorName ?? ''}
                  onChange={(e) => onMedicalDataChange({ primaryDoctorName: e.target.value })}
                  className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                  placeholder="Dr. Jane Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={medicalData.primaryDoctorPhone ?? ''}
                  onChange={(e) => onMedicalDataChange({ primaryDoctorPhone: e.target.value })}
                  className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                  placeholder="+1 (555) 123-4567"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Address
                </label>
                <textarea
                  value={medicalData.primaryDoctorAddress ?? ''}
                  onChange={(e) => onMedicalDataChange({ primaryDoctorAddress: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                  placeholder="123 Main St, Suite 100, City, State 12345"
                />
              </div>
            </div>
          )}
        </div>

        {/* Medical Conditions */}
        <div className="pt-4 border-t border-secondary-200 dark:border-secondary-700 space-y-4">
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Medical Conditions</h3>

          {!isEditing ? (
            <>
              {medicalData.medicalConditions && medicalData.medicalConditions.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {medicalData.medicalConditions.map((condition, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-primary-100 dark:bg-primary-900/20 text-primary-800 dark:text-primary-300 text-sm rounded-full"
                    >
                      {condition}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-secondary-500 dark:text-secondary-400">No medical conditions on file</p>
              )}
            </>
          ) : (
            <div className="space-y-3">
              {medicalData.medicalConditions && medicalData.medicalConditions.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {medicalData.medicalConditions.map((condition, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-primary-100 dark:bg-primary-900/20 text-primary-800 dark:text-primary-300 text-sm rounded-full flex items-center gap-2"
                    >
                      {condition}
                      <button
                        onClick={() => removeMedicalCondition(index)}
                        className="hover:text-primary-900 dark:hover:text-primary-100"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newCondition}
                  onChange={(e) => setNewCondition(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addMedicalCondition()}
                  className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white ${
                    newCondition.trim() ? 'border-primary-500' : 'border-secondary-300 dark:border-secondary-600'
                  }`}
                  placeholder="e.g., Pacemaker, Broken leg, Diabetes"
                />
                <Button
                  type="button"
                  onClick={addMedicalCondition}
                  variant={newCondition.trim() ? 'default' : 'outline'}
                  size="sm"
                  aria-label="Add medical condition"
                  className={newCondition.trim() ? 'animate-pulse ring-2 ring-primary-400 ring-offset-1 dark:ring-offset-secondary-900' : undefined}
                >
                  <PlusIcon className="w-4 h-4" />
                </Button>
              </div>
              {newCondition.trim() && (
                <p className="text-xs text-primary-600 dark:text-primary-400">
                  Press <span className="font-semibold">+</span> (or Enter) to add this condition.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Medical Disclosure Consent */}
        <div className="pt-4 border-t border-secondary-200 dark:border-secondary-700 space-y-4">
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-white flex items-center">
            <DocumentIcon className="w-5 h-5 mr-2" />
            Medical Disclosure Consent
          </h3>

          {!isEditing ? (
            <div className="space-y-3">
              <div>
                <div className="text-sm text-secondary-600 dark:text-secondary-400">Consent Status</div>
                <div className={cn(
                  'font-medium',
                  medicalData.medicalDisclosureConsent ? 'text-success-600 dark:text-success-400' : 'text-secondary-900 dark:text-white'
                )}>
                  {medicalData.medicalDisclosureConsent ? 'Approved' : 'Not Approved'}
                </div>
              </div>
              {medicalData.medicalDisclosureConsent && (
                <>
                  {medicalData.consentDate && (
                    <div>
                      <div className="text-sm text-secondary-600 dark:text-secondary-400">Consent Date</div>
                      <div className="font-medium text-secondary-900 dark:text-white">{formatDate(medicalData.consentDate)}</div>
                    </div>
                  )}
                  {medicalData.consentExpiresAt && (
                    <div>
                      <div className="text-sm text-secondary-600 dark:text-secondary-400">Expires On</div>
                      <div className="font-medium text-secondary-900 dark:text-white">{formatDate(medicalData.consentExpiresAt)}</div>
                    </div>
                  )}

                  {/* Consent Form Document */}
                  {consentForm && (
                    <div className="pt-3 border-t border-secondary-200 dark:border-secondary-700">
                      <div className="text-sm text-secondary-600 dark:text-secondary-400 mb-2">Consent Form Document</div>
                      <div className="flex items-center justify-between p-3 bg-secondary-50 dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700">
                        <div className="flex items-center space-x-3">
                          <DocumentIcon className="w-6 h-6 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                          <div>
                            <div className="font-medium text-secondary-900 dark:text-white">{consentForm.filename}</div>
                            <div className="text-xs text-secondary-600 dark:text-secondary-400">
                              Uploaded {formatDate(consentForm.uploadedAt)}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDownloadAttachment(consentForm)}
                          className="p-2 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                          title="Download consent form"
                        >
                          <ArrowDownTrayIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="medicalConsent"
                  checked={medicalData.medicalDisclosureConsent ?? false}
                  onChange={(e) => onMedicalDataChange({ medicalDisclosureConsent: e.target.checked })}
                  className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
                />
                <label
                  htmlFor="medicalConsent"
                  className="ml-2 text-sm text-secondary-700 dark:text-secondary-300"
                >
                  I authorize the disclosure of medical information for emergency purposes
                </label>
              </div>

              {medicalData.medicalDisclosureConsent && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <DateInput
                      label="Consent Date"
                      value={formatDateForInput(medicalData.consentDate)}
                      onChange={(v) => onMedicalDataChange({ consentDate: v })}
                    />
                  </div>
                  <div>
                    <DateInput
                      label="Expires On"
                      value={formatDateForInput(medicalData.consentExpiresAt)}
                      onChange={(v) => onMedicalDataChange({ consentExpiresAt: v })}
                      error={errors.consentExpiresAt}
                    />
                  </div>
                </div>
              )}

              {/* Consent Form Upload */}
              {onConsentFormUpload && medicalData.medicalDisclosureConsent && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300">
                    Signed Consent Form
                  </label>
                  {consentForm ? (
                    <div className="flex items-center justify-between p-3 bg-secondary-50 dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700">
                      <div className="flex items-center space-x-3">
                        <DocumentIcon className="w-6 h-6 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                        <div>
                          <div className="font-medium text-secondary-900 dark:text-white">{consentForm.filename}</div>
                          <div className="text-xs text-secondary-600 dark:text-secondary-400">
                            Uploaded {formatDate(consentForm.uploadedAt)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDownloadAttachment(consentForm)}
                          className="p-2 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                          title="Download consent form"
                        >
                          <ArrowDownTrayIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteAttachment(consentForm.id)}
                          className="p-2 text-danger-600 hover:text-danger-700 dark:text-danger-400 dark:hover:text-danger-300 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded-lg transition-colors"
                          title="Delete consent form"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <ImageUpload
                        onImageSelect={async (file) => {
                          if (onConsentFormUpload) {
                            await onConsentFormUpload(file);
                            // Refresh attachments locally to show the uploaded file
                            void fetchMedicalAttachments();
                          }
                        }}
                        maxSizeMB={50}
                        acceptedFormats={['application/pdf', 'image/jpeg', 'image/jpg', 'image/png']}
                      />
                      <p className="text-xs text-secondary-500 dark:text-secondary-400">
                        PDF or image files (max 50MB)
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>

      {/* Image Preview Modal */}
      {previewImage && (
        <ImagePreviewModal
          isOpen={!!previewImage}
          onClose={() => setPreviewImage(null)}
          imageUrl={previewImage}
          altText="Insurance Card"
        />
      )}
    </Card>
  );
}
